import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Icon from '../AppIcon';

const DashboardSidebar = ({ isCollapsed = false, onToggle }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [userRole, setUserRole] = useState(null);
  const [aziendaNome, setAziendaNome] = useState('');
  const [aziendaLogo, setAziendaLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  useEffect(() => {
    const fetchSidebarData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // 1. Dohvatanje podataka o firmi iz profila korisnika
        const { data: profil, error: profError } = await supabase
          .from('profili')
          .select('ruolo, nome_azienda, id_azienda')
          .eq('id', user.id)
          .single();

        if (profil && !profError) {
          setUserRole(profil.ruolo);
          setAziendaNome(profil.nome_azienda || 'OrizonDesk');

          // 2. Logika za učitavanje logotipa iz Storage-a
          if (profil.id_azienda) {
            const folderPath = `${profil.id_azienda}/logo_aziende`;
            
            // Izlistavamo fajlove u specifičnom folderu kompanije
            const { data: files } = await supabase
              .storage
              .from('aziende')
              .list(folderPath);

            if (files && files.length > 0) {
              // Filtriramo sistemske fajlove i uzimamo prvi pravi fajl (sliku)
              const imageFile = files.find(f => f.name !== '.emptyFolderPlaceholder');
              
              if (imageFile) {
                /** * Korišćenje createSignedUrl rešava problem sa RLS polisama (upitnik na slici).
                 * Generiše privremeni sigurni link koji važi 24 sata.
                 */
                const { data: signedData } = await supabase
                  .storage
                  .from('aziende')
                  .createSignedUrl(`${folderPath}/${imageFile.name}`, 86400); 

                if (signedData) {
                  setAziendaLogo(signedData.signedUrl);
                }
              }
            }
          }
        }
      } catch (err) {
        console.error("Sidebar data fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSidebarData();
  }, []);

  const navigationItems = [
    { id: 'home', labelKey: 'sidebar.dashboard', route: '/', icon: 'LayoutDashboard' },
    { id: 'profile', labelKey: 'sidebar.profile', route: '/mio-profilo', icon: 'User' },
    { id: 'settings', labelKey: 'sidebar.settings', route: '/settings', icon: 'Settings' }
  ];

  const handleNavigation = (item) => {
    if (item.id === 'home') {
      if (userRole === 'User') navigate('/home-dashboard-dipendente');
      else if (userRole === 'Owner') navigate('/home-dashboard-owner');
      else navigate('/home-dashboard');
    } else if (item.id === 'profile') {
      navigate(item.route);
      if (!isCollapsed) setIsProfileOpen((prev) => !prev);
    } else {
      navigate(item.route);
    }
  };

  const isActive = (route, itemId) => {
    if (itemId === 'home') {
      return ['/home-dashboard', '/home-dashboard-dipendente', '/home-dashboard-owner', '/'].includes(location.pathname);
    }
    return location?.pathname === route;
  };

  // Otvori submenu automatski ako je korisnik vec na /my-holidays
  useEffect(() => {
    if (location.pathname === '/my-holidays') setIsProfileOpen(true);
  }, [location.pathname]);

  return (
    <>
      <style>{`
        @media (max-width: 768px) { .desktop-sidebar { display: none !important; } }
        .sidebar-nav-item { transition: all 0.2s ease !important; border: none !important; outline: none !important; }
        .sidebar-nav-item:hover { background: rgba(var(--color-primary-rgb), 0.08) !important; transform: translateX(4px); }
        .sidebar-nav-item.active { background: rgba(var(--color-primary-rgb), 0.12) !important; color: var(--color-primary) !important; }
        .sidebar-submenu-item { transition: all 0.2s ease !important; border: none !important; outline: none !important; }
        .sidebar-submenu-item:hover { background: rgba(var(--color-primary-rgb), 0.08) !important; transform: translateX(4px); }
        .sidebar-submenu-item.active { background: rgba(var(--color-primary-rgb), 0.12) !important; color: var(--color-primary) !important; }
        .sidebar-submenu-wrapper { overflow: hidden; transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>

      <aside 
        className={`sidebar desktop-sidebar ${isCollapsed ? 'collapsed' : ''}`}
        style={{ 
          width: isCollapsed ? '80px' : '260px',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex', flexDirection: 'column', height: '100vh',
          position: 'fixed', left: 0, top: 0, zIndex: 100,
          background: 'var(--color-card)', borderRight: '1px solid var(--color-border)',
          paddingTop: '90px' 
        }}
      >
        <button 
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          style={{ 
            position: 'absolute', right: '-12px', top: '110px', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            width: '24px', height: '24px', borderRadius: '50%', 
            background: 'var(--color-primary)', border: '2px solid var(--color-card)',
            color: 'white', cursor: 'pointer', zIndex: 101, boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={14} />
        </button>

        <nav style={{ flex: 1, padding: '0 12px' }}>
          {navigationItems.map((item) => {
            const active = isActive(item.route, item.id);
            const isProfile = item.id === 'profile';
            const submenuActive = isProfile && location.pathname === '/my-holidays';

            return (
              <div key={item.id}>
                <button
                  onClick={() => handleNavigation(item)}
                  className={`sidebar-nav-item ${active ? 'active' : ''}`}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 12px', marginBottom: '6px',
                    borderRadius: '12px', background: 'transparent',
                    color: active ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ minWidth: '24px', display: 'flex', justifyContent: 'center' }}>
                      <Icon name={item.icon} size={20} strokeWidth={active ? 2.5 : 2} />
                    </div>
                    {!isCollapsed && (
                      <span style={{ marginLeft: '12px', fontSize: '0.85rem', fontWeight: active ? 700 : 500, textTransform: 'uppercase' }}>
                        {t(item.labelKey)}
                      </span>
                    )}
                  </div>
                  {isProfile && !isCollapsed && (
                    <Icon
                      name="ChevronDown"
                      size={14}
                      style={{
                        transform: isProfileOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        opacity: 0.6
                      }}
                    />
                  )}
                </button>

                {isProfile && !isCollapsed && (
                  <div
                    className="sidebar-submenu-wrapper"
                    style={{ maxHeight: isProfileOpen ? '60px' : '0px' }}
                  >
                    <button
                      onClick={() => navigate('/my-holidays')}
                      className={`sidebar-submenu-item ${submenuActive ? 'active' : ''}`}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', padding: '12px 12px 12px 24px', marginBottom: '6px',
                        borderRadius: '12px', background: 'transparent',
                        color: submenuActive ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                        cursor: 'pointer', textAlign: 'left'
                      }}
                    >
                      <div style={{ minWidth: '20px', display: 'flex', justifyContent: 'center' }}>
                        <Icon name="Palmtree" size={17} strokeWidth={submenuActive ? 2.5 : 2} />
                      </div>
                      <span style={{ marginLeft: '10px', fontSize: '0.78rem', fontWeight: submenuActive ? 700 : 500, textTransform: 'uppercase' }}>
  {t('sidebar.my_holidays')}
</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* SEKCIJA SA LOGOTIPOM FIRME */}
        <div style={{ 
          margin: '0 12px 10px 12px',
          padding: '12px',
          borderRadius: '12px',
          background: 'rgba(var(--color-primary-rgb), 0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          minHeight: '52px',
          border: '1px solid var(--color-border)'
        }}>
          {aziendaLogo ? (
            <img 
              src={aziendaLogo} 
              alt="Logo" 
              style={{ width: '28px', height: '28px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} 
              onError={() => setAziendaLogo(null)} // Fail-safe: ako link istekne, prikaži inicijale
            />
          ) : (
            <div style={{ 
              width: '28px', height: '28px', borderRadius: '6px', background: 'var(--color-primary)', 
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold', flexShrink: 0
            }}>
              {loading ? '...' : aziendaNome.substring(0, 2).toUpperCase()}
            </div>
          )}

          {!isCollapsed && (
            <div style={{ marginLeft: '10px', overflow: 'hidden', flex: 1 }}>
              <p style={{ 
                margin: 0, fontSize: '0.75rem', fontWeight: '800', 
                color: 'var(--color-foreground)', whiteSpace: 'nowrap', 
                textOverflow: 'ellipsis', overflow: 'hidden' 
              }}>
                {loading ? '...' : aziendaNome}
              </p>
            </div>
          )}
        </div>

        <div style={{ paddingBottom: '20px', opacity: 0.4, textAlign: 'center' }}>
          <span style={{ fontSize: '9px', fontWeight: 900, color: 'var(--color-muted-foreground)', letterSpacing: '0.15em' }}>
            {t('sidebar.version')}
          </span>
        </div>
      </aside>
    </>
  );
};

export default DashboardSidebar;